import React, { useState, Fragment } from "react";
import {
	useTable,
	useFlexLayout,
	useExpanded,
	useResizeColumns,
} from "react-table";
import { formatValue } from "./Utils.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { UtilizationCharts } from "./UtilizationCharts.jsx";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { useNavigate } from "react-router-dom";
// import { Tooltip } from 'react-tooltip';
import Tooltip from "./form-components/Tooltip.jsx";

import "./ResourceTable.css";


function Table({ columns, data, setHoveredRow, expandAll, toggleRow }) {
	const openResourceTypes = sessionStorage.getItem('openResourceTypes');
	const openRows = openResourceTypes ? openResourceTypes.split(",") : [];
	let defaultExpandedRows = {};

	data.forEach((element, index) => {
		if (openRows.includes(element.resource_type) || expandAll) {
			defaultExpandedRows[index] = true;
		}
	});

	const defaultColumn = React.useMemo(
		() => ({
		  minWidth: 30,
		  width: 150,
		  maxWidth: 800,
		}),
		[]
	  )

	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		useTable(
			{
				columns,
				data,
				defaultColumn,
				autoResetExpanded: false,
				collapseOnDataChange: false,
				initialState: { expanded: defaultExpandedRows },
			},
			useFlexLayout,
			useExpanded,
			useResizeColumns
		);

	return (
		<div
			{...getTableProps()}
			className="flex w-full cursor-default flex-auto flex-col"
		>
			<div className="py-2 pl-4 text-text-2 sticky top-0 z-50 ">
				{headerGroups.map((headerGroup) => {
					const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
					return (
						<div key={headerGroupKey} {...headerGroupProps} className="tr">
							{headerGroup.headers.map((column) => {
								const { key: columnKey, ...columnHeaderProps } = column.getHeaderProps();
								return (
									<div
										key={columnKey}
										{...columnHeaderProps}
										className="th uppercase text-sm"
									>
										{column.render("Header")}
										<div
											{...column.getResizerProps()}
											className={`resizer ${column.isResizing ? "isResizing" : ""}`}
										/>
									</div>
								);
							})}
						</div>
					);
				})}
			</div>

			<div {...getTableBodyProps()} className="bg-card border border-card-bd mr-6 rounded-card">
				{rows.map((row, index) => {
					prepareRow(row);
					const { key, ...rowProps } = row.getRowProps({});

					return (
						<div
							key={key}
							{...rowProps}
							className={
								"tr border-b border-border hover:bg-surface-2 h-[77px] cursor-pointer pl-4" +
								(index === 0 ? " rounded-t-[16px]" : "") +
								(index === rows.length - 1 ? " rounded-b-[16px] border-b-0" : "")
							}
							onClick={(e) => {
								// Check if this row can be expanded
								if (row.original.subRows && row.original.subRows.length > 0) {
									row.toggleRowExpanded();
									if (toggleRow) toggleRow(row.original.resource_type);
								}
							}}
						>
							{row.cells.map((cell) => {
								const { key: cellKey, ...cellProps } = cell.getCellProps();
								return (
									<div
										key={cellKey}
										{...cellProps}
										className="td truncate pl-1 pt-6 pb-4 text-sm"
									>
										{cell.render("Cell")}
									</div>
								);
							})}
						</div>
					);
				})}
			</div>
		</div>
	);
}

const ResourceTable = ({ data, grouping, toggleRow, openRows, expandAll }) => {
	const [, setHoveredRow] = useState(-1);
	const navigate = useNavigate();

	const openDetails = (resourceID, type) => {
		if (type === "Resource") {
			navigate("/resource/" + resourceID);
		}
	}

	const columnDefs = [
		{
			accessor: "plus",
			id: "expander",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="The name of the resource">
						Resource
				</span>
			),
			Cell: ({ row }) => {
				const toggleRowExpandedProps = row.getToggleRowExpandedProps();
				const onClick = async (event) => {
					toggleRowExpandedProps.onClick(event);

					if (toggleRow) toggleRow(row.original.resource_type);
					// if (!row.isExpanded && !row.original.subRows) {
						// retrieveAssets(row.original.resource_type);
					// }
				};

				const margin = {
                    paddingLeft: 36 + (row.depth*36)
                }

				if (!row.original.resource_name) {
					if (row.depth === 1 && row.original.subRows.length > 0) {
						// if the depth = 1 and there are subrows, show the expander but put padding on the left
						const { key: toggleKey1, ...toggleProps1 } = row.getToggleRowExpandedProps();
						return (
							<>
							<span key={toggleKey1} {...toggleProps1} onClick={(e) => { e.stopPropagation(); onClick(e); }} className="text-left text-text font-semibold pl-12 cursor-pointer pt-1">
								{row.isExpanded ? (
									<FontAwesomeIcon
										icon={icon({
											name: "angle-down",
											family: "sharp",
											style: "solid",
										})}
										className="text-text-2 mr-2"
									/>
								) : (
									<FontAwesomeIcon
										icon={icon({
											name: "angle-right",
											family: "sharp",
											style: "solid",
										})}
										className="text-text-2 mr-2"
									/>
								)}
								{row.original.resource_type}
							</span>
							</>
						)							
					} else {
						// if the row is a resource group, show the expander
						const { key: toggleKey2, ...toggleProps2 } = row.getToggleRowExpandedProps();
						return (
							<>
							<span key={toggleKey2} {...toggleProps2} onClick={(e) => { e.stopPropagation(); onClick(e); }} className="text-left text-text font-semibold cursor-pointer pt-1">
								{row.isExpanded ? (
									<FontAwesomeIcon
										icon={icon({
											name: "angle-down",
											family: "sharp",
											style: "solid",
										})}
										className="text-text-2 mr-2"
									/>
								) : (
									<FontAwesomeIcon
										icon={icon({
											name: "angle-right",
											family: "sharp",
											style: "solid",
										})}
										className="text-text-2 mr-2"
									/>
								)}
								{row.original.resource_cluster && row.original.resource_cluster}
								{row.original.resource_type && row.original.resource_type}
							</span>
							</>
						)							
					}
				} else {
					return (
						<div 
							style={margin}
							className="text-left text-text cursor-pointer pt-1"
							onClick={(e) => { e.stopPropagation(); openDetails(row.original.resource_md5, row.original.type)} }
						>
							{row.original.resource_name}
						</div>
					);
				}
			},
			// minWidth: 30,
			width: 500,
			// maxWidth: 30,
		},
		{
			accessor: "vendor_name",
			// Header: (row) => <div className="">Vendor</div>,
			Cell: ({ row }) => {
				if (!row.original.resource_name || row.original.type === "Product") {
					return (
						<div />
					);
				} else {
					return (
						<div className="text-center text-text font-semibold pt-1">
					 		<FontAwesomeIcon icon={faGoogle} className="text-text-2" />
						</div>
					);
				}
			},
			minWidth: 50,
			width: 50,
			maxWidth: 50,
		},
		{
			accessor: "resource_type",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="The type of the resource">
					Type
				</span>
			),
			Cell: (cell) => {
				if (cell.row.original.resource_name) {
					return (
						<div className="text-left text-text pt-1">
							{cell.row.original.resource_type}
						</div>
					);
				}
			},
			width: 100,
		},
		{
			accessor: "units",
			Header: (row) => (
				<div className="text-right mr-2" 
				>
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The number of units of the resource that is currently being used">
						Units
					</span>
				</div>
			),
			Cell: ({row}) => {
				let valueClass = "text-right text-text pt-1 ";
				if (row.original.subRows && row.original.subRows.length > 0) valueClass += "font-semibold";
				return (
					<div className={valueClass}>
						{formatValue(row.original.units, "decimal", false)}
					</div>
					)	
			},
			width: 100,
		},
		{
			accessor: "resource_status",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="is the resource currently active or inactive">
					<span className="flex justify-center items-center w-full">Status</span>
				</span>
			),
			Cell: (cell) => {
				if (cell.row.original.resource_name) {
					return (
						<div className="text-center pt-1">
							<span
								className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold
									${cell.value && cell.value.toLowerCase() === "active"
										? "bg-success-soft text-success border border-success"
										: cell.value && cell.value.toLowerCase() === "inactive"
											? "bg-danger-soft text-danger border border-danger"
											: "bg-surface-2 text-text-2 border border-border"
									}`
								}
							>
								{cell.value}
							</span>
						</div>
					);						
				}
			},
			width: 100,
            maxWidth: 100,
            minWidth: 100,
		},		
		{
			accessor: "cost",
			Header: (row) => (
				<div className="text-right mr-2">
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The cost of the resource during the selected time period">
						Cost
					</span>
				</div>
			),
			Cell: ({row}) => {
				let valueClass = "text-right text-text pt-1 ";
				if (row.original.subRows && row.original.subRows.length > 0) valueClass += "font-semibold";
				
				if (row.original.costs_not_known) {
					return (
						<div className={valueClass}>
							
						</div>
					)
				} else {
					return (
						<div className={valueClass}>
							{formatValue(row.original.cost, "currency", false)}
						</div>
					)
				}
			},
			width: 140
		},
		{
			accessor: "insights",
			Cell: ({row}) => {
				if (!row.original.resource_name || row.original.type === "Product") {
					return (
						<div />
					);
				} else {
					if (row.original.insights > 0) {
						return (
							<div className="text-center text-text text-lg font-semibold">
								<FontAwesomeIcon
									icon={icon({
										name: "lightbulb",
										family: "sharp",
										style: "solid",
									})}
									className="text-warning"
								/>
							</div>
							)
					} else {
						return (
							<div />
						)
					}
				}
			},
			width: 50,
		},
		// {
		// 	accessor: "usage_amount",
		// 	Header: (row) => <div className="text-right mr-2">Usage</div>,
		// 	Cell: (row) => (
		// 		<div className="text-right text-text font-semibold">
		// 			{formatValue(row.value, "decimal", false)}
		// 		</div>
		// 	),
		// 	width: 100,
		// 	maxWidth: 100,
		// },
		{
			accessor: "utilization",
			Header: (row) => (
				<div className="text-left ml-4">
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The utilization of the resource during the selected time period">
						Utilization
					</span>
				</div>
			),
			Cell: (row) => {
				if (!row.row.original.resource_name || row.row.original.type === "Product") {
					return (
						<div />
					);
				} else {
					return (
						<UtilizationCharts data={row.row.original} />
					);
				}
			},
			// Cell: (row) => <UtilizationCharts data={row.row.original} />,
			width: 300,
			maxWidth: 300,
		},
		// {
		// 	accessor: "actions",
		// 	Header: (row) => <div className="text-left mr-4 ml-4">Actions</div>,
		// 	Cell: (row) => {
		// 		const navigate = useNavigate();
		// 		const openDetails = (productID) => {
        //             navigate("/product-overview/" + productID)
        //         }

		// 		if (row.row.original.resource_name) {
		// 			return (
		// 				<div className={"text-left text-text font-semibold mr-4 " + (row.row.id == hoveredRow ? "flex" : "hidden")}>
		// 				<div className="border rounded-lg cursor-pointer px-2 w-[80px] leading-6 bg-lightblue3 text-text0" onClick={(e) => openDetails(row.row.original.product_id)}>
		// 					<FontAwesomeIcon
		// 						icon={icon({
		// 							name: "arrow-right",
		// 							family: "sharp",
		// 							style: "solid",
		// 						})}
		// 						className="text-text0 mr-1"
		// 					/>
		// 					Details
		// 				</div>
		// 			</div>
						
		// 			)
		// 		} else {
		// 			return <div></div>
		// 		}
		// 	},
		// 	width: 100,
		// 	maxWidth: 100,
		// },
	];

	if (data && data.length > 0) {
		return (
			<Fragment>
					<Tooltip />
					<Table
						className="flex-auto"
						columns={columnDefs}
						data={data}
						renderRowSubComponent
						expandedRows={false}
						openRows={openRows}
						setHoveredRow={setHoveredRow}
						expandAll={expandAll}
						toggleRow={toggleRow}
					/>
			</Fragment>
		);
	} else {
		return (
			<Fragment>
				<div className="font-regular text-base text-text"></div>
			</Fragment>
		);
	}
};

export default ResourceTable;
